import { useCallback, useState } from 'react';

export interface UseBooleanReturn {
  (value?: boolean): void;
  on: () => void;
  off: () => void;
  toggle: () => void;
}

/**
 * Chakra v2's `useBoolean` was removed outright in v3 with no replacement
 * export. Small, direct re-implementation matching its real API (a setter
 * that's both callable — `setFlag(true)` or `setFlag()` to toggle — and
 * carries `.on()`/`.off()`/`.toggle()` methods).
 */
export default function useBoolean(
  initialValue = false,
): [boolean, UseBooleanReturn] {
  const [value, setValue] = useState(initialValue);

  const on = useCallback(() => setValue(true), []);
  const off = useCallback(() => setValue(false), []);
  const toggle = useCallback(() => setValue((prev) => !prev), []);

  const setBoolean = useCallback(
    (next?: boolean) => {
      if (typeof next === 'boolean') {
        setValue(next);
      } else {
        toggle();
      }
    },
    [toggle],
  ) as UseBooleanReturn;

  setBoolean.on = on;
  setBoolean.off = off;
  setBoolean.toggle = toggle;

  return [value, setBoolean];
}
